import { reactive } from 'vue'
import { deepCopy } from '@/util/deep-copy'
import store from '@/store/index'
import { typeToString } from '@/util/type-to-string'

/*
 * 시트 필터 — 구 V2ReservationManagementDataController 의 필터 표면 (MDP-2295 ④-5 2단계).
 *
 * 하는 일은 하나다: 서버가 준 목록에 이 브라우저가 기억하는 "체크 상태" 를 얹는다.
 * 기억은 vuex(V2Reservation)에 있고 vuex-persistedstate 가 localStorage 로 넘기므로,
 * 새로 고쳐도 내가 끈 항목은 꺼진 채로 남는다.
 *
 * 병합은 두 방향에서 들어온다.
 *   - 서버에서 온 목록: isSelected 가 없다 → 기억해 둔 값을 얹는다
 *   - 사용자가 만진 목록: isSelected 가 있다 → 그 값을 기억에 쓴다
 * 같은 함수가 둘을 겸하는 것은 구 setter 의 모양을 그대로 옮긴 것이다.
 *
 * 지금 옮긴 것은 예약상태뿐이다(2-a). 담당자·그룹은 뒤따른다 — 셋은 순서를 정하는
 * 규칙이 서로 달라(상태는 서버 sortNum, 담당자는 그룹 안 순서, 그룹은 기억해 둔 순서)
 * 하나로 합치지 않고 각각 옮긴다.
 */

/** 목록 한 항목 중 이 모듈이 쓰는 부분. 나머지 필드는 그대로 통과시킨다. */
export type FilterItem = {
    _id?: string
    isSelected?: boolean
    sortNum?: number
    [extra: string]: unknown
}

/** vuex 에 기억해 두는 항목 */
type StoredItem = { _id?: string; isSelected?: boolean; order?: number; groupID?: unknown }


const currentHospitalID = (): string => store.state.hospital?.selectedHospital?._id ?? ''

const storedStates = (hospitalID: string): StoredItem[] =>
    deepCopy(store.state.V2Reservation.hospitals[hospitalID]?.states) ?? []

const storedColumnsOf = (hospitalID: string): StoredItem[] =>
    deepCopy(store.state.V2Reservation.hospitals[hospitalID]?.columns) ?? []

const storedGroupsOf = (hospitalID: string): StoredItem[] =>
    deepCopy(store.state.V2Reservation.hospitals[hospitalID]?.groups) ?? []

export const sheetFilters = reactive({
    states: [] as FilterItem[],
    columns: [] as FilterItem[],
    groups: [] as FilterItem[],

    /*
     * 시트가 실제로 그리는 그룹 — 필터를 거치고 담당자가 붙은 결과다.
     * ReservationSheet 가 계산해 넣고 시간축(TimeField·TimeItem)과 엑셀저장이 읽는다.
     * 병합할 것이 없어 함수를 두지 않고 그대로 대입한다.
     */
    usableGroups: [] as FilterItem[],
})

/**
 * 예약상태 목록을 갈아 끼운다 (구 originalStates setter).
 *
 * 인자로 받은 배열을 제자리에서 고친 뒤 보관한다 — 구 setter 가 그랬고, 호출부가 넘긴
 * 배열이 그대로 쓰이는 곳이 있어 그 동작을 유지한다.
 */
export function setStates(array: FilterItem[]): void {
    const hospitalID = currentHospitalID()
    let states = storedStates(hospitalID)

    // 서버에 없어진 상태는 기억에서도 지운다
    const ids = array.map((v) => v._id)
    states = states.filter((s) => ids.includes(s._id))

    for (let i = 0; i < array.length; i++) {
        const state = array[i]
        const stateID = state._id
        const storedState = states.find((s) => s._id == stateID)

        if (storedState == undefined) {
            // isEmpty
            states.push({ _id: state._id, isSelected: true, order: state.sortNum })
        } else if (state.isSelected == undefined) {
            // from network
            state.isSelected = storedState.isSelected
        } else {
            // from user action
            const index = states.indexOf(storedState)
            states[index].isSelected = state.isSelected
        }
    }

    // set order & sort
    array.forEach((state) => {
        const s = states.find((v) => v._id == state._id)
        state.isSelected = s?.isSelected
    })

    array.sort((l, r) => ((l.sortNum ?? 0) <= (r.sortNum ?? 0) ? -1 : 1))

    // store
    store.commit('MU_V2Reservation_SetStates', { hospitalID, states })

    sheetFilters.states = deepCopy(array) ?? []
}

/** 하나 켜고 끄기 (구 updateSelectedState) */
export function toggleState(index: number): void {
    const newObject = deepCopy(sheetFilters.states[index]) as FilterItem
    newObject.isSelected = !newObject.isSelected
    sheetFilters.states.splice(index, 1, newObject)
    setStates(deepCopy(sheetFilters.states) ?? [])
}

/** 전부 켜거나 끄기 (구 updateAllSelectedStates) */
export function toggleAllStates(isSelected: boolean): void {
    sheetFilters.states.forEach((s) => (s.isSelected = isSelected))
    setStates(deepCopy(sheetFilters.states) ?? [])
}

/** 켜져 있는 것만 (구 selectedStates getter) */
export function selectedStates(): FilterItem[] {
    return sheetFilters.states.filter((s) => s.isSelected)
}

// ─────────────────────────────── 담당자 ───────────────────────────────

/** vuex 에 기억해 둔 담당자 목록 (구 storedColumn getter — 단수 이름이었다) */
export function storedColumns(): FilterItem[] {
    return storedColumnsOf(currentHospitalID()) as FilterItem[]
}

/** 서버에서 사라진 담당자를 기억에서도 지운다 (구 removeStoredColumns) */
export function removeStoredColumns(columnIDs: string[]): void {
    if (columnIDs.length == 0) {
        return
    }
    const hospitalID = currentHospitalID()
    const columns = storedColumnsOf(hospitalID)
    for (const id of columnIDs) {
        const target = columns.find((c) => c._id == id)
        const index = columns.indexOf(target as StoredItem)
        if (index < 0) {
            continue
        }
        columns.splice(index, 1)
    }
    store.commit('MU_V2Reservation_SetColumns', { hospitalID, columns })
}

/**
 * 담당자 목록을 갈아 끼운다 (구 originalColumns setter).
 *
 * 예약상태와 다른 점 둘 — 순서를 그룹 안에서 매기고(새 담당자는 그 그룹의 맨 뒤),
 * groupID 도 기억해 둔 값으로 되돌린다. 그래서 setStates 와 합치지 않았다.
 */
export function setColumns(array: FilterItem[]): void {
    const hospitalID = currentHospitalID()
    const columns = storedColumnsOf(hospitalID)

    // fill store array
    const columnDic: Record<string, FilterItem[]> = {}
    for (let i = 0; i < array.length; i++) {
        const column = array[i]
        const columnID = column._id
        const storedColumn = columns.find((c) => c._id == columnID)
        let groupID = column.groupID
        if (typeToString(groupID) == 'object') {
            groupID = (groupID as { _id?: string })._id
        }
        const groupKey = String(groupID)
        if (columnDic[groupKey] == undefined) {
            columnDic[groupKey] = []
        }
        columnDic[groupKey].push(column)

        if (storedColumn == undefined) {
            // isEmpty
            const order = columnDic[groupKey].length - 1
            columns.push({ _id: column._id, isSelected: true, groupID, order })
        } else if (column.isSelected == undefined) {
            // from network
            column.isSelected = storedColumn.isSelected
        } else {
            // from user action
            const index = columns.indexOf(storedColumn)
            columns[index].isSelected = column.isSelected
        }
    }

    // set order & sort
    array.forEach((column) => {
        const c = columns.find((v) => v._id == column._id)
        column.groupID = c?.groupID
        column.order = c?.order
        column.isSelected = c?.isSelected
    })
    array.sort((l, r) => (l.order as number) - (r.order as number))

    // store
    store.commit('MU_V2Reservation_UpdateColumns', { hospitalID, columns })

    sheetFilters.columns = deepCopy(array) ?? []
}

/**
 * 바꾼 순서·선택을 기억에 쓰고 목록을 다시 맞춘다 (구 updateColumns).
 * setColumns 와 달리 인자는 vuex mutation 이 받는 모양({ hospitalID, columns }) 이다.
 */
export function saveColumns(data: { hospitalID: string; columns: FilterItem[] }): void {
    store.commit('MU_V2Reservation_UpdateColumns', deepCopy(data))

    const hospitalID = currentHospitalID()
    const stored = storedColumnsOf(hospitalID)
    const newArray = (deepCopy(sheetFilters.columns) ?? []) as FilterItem[]
    newArray.forEach((c) => {
        const storedC = stored.find((s) => s._id == c._id)
        c.groupID = storedC?.groupID
        c.order = storedC?.order
        c.isSelected = storedC?.isSelected
    })

    newArray.sort((l, r) => ((l.order as number) <= (r.order as number) ? -1 : 1))

    sheetFilters.columns = deepCopy(newArray) ?? []
}

/** 하나 켜고 끄기 (구 updateSelectedColumn) */
export function toggleColumn(index: number): void {
    const newObject = deepCopy(sheetFilters.columns[index]) as FilterItem
    newObject.isSelected = !newObject.isSelected
    const columns = (deepCopy(sheetFilters.columns) ?? []) as FilterItem[]
    columns.splice(index, 1, newObject)
    setColumns(columns)
}

/** 전부 켜거나 끄기 (구 updateAllSelectedColumn) */
export function toggleAllColumns(isSelected: boolean): void {
    const columns = (deepCopy(sheetFilters.columns) ?? []) as FilterItem[]
    columns.forEach((c) => (c.isSelected = isSelected))
    setColumns(deepCopy(columns) ?? [])
}

/** 켜져 있는 것만 (구 selectedColumns getter) */
export function selectedColumns(): FilterItem[] {
    return sheetFilters.columns.filter((c) => c.isSelected)
}

// ──────────────────────────────── 그룹 ────────────────────────────────

/** vuex 에 기억해 둔 그룹 목록 (구 storedGroups getter) */
export function storedGroups(): FilterItem[] {
    return storedGroupsOf(currentHospitalID()) as FilterItem[]
}

/** 서버에서 사라진 그룹을 기억에서도 지운다 (구 removeStoredGroups) */
export function removeStoredGroups(groupIDs: string[]): void {
    const hospitalID = currentHospitalID()
    const groups = storedGroupsOf(hospitalID)
    for (const id of groupIDs) {
        const target = groups.find((g) => g._id == id)
        const index = groups.indexOf(target as StoredItem)
        if (index < 0) {
            continue
        }
        groups.splice(index, 1)
    }
    store.commit('MU_V2Reservation_SetGroups', { hospitalID, groups })
}

/**
 * 그룹 목록을 갈아 끼운다 (구 originalGroups setter).
 *
 * 담당자와 다른 점 — 순서를 기억해 둔 값에서 그대로 가져오고, 새 그룹은 맨 뒤에 붙인다.
 */
export function setGroups(array: FilterItem[]): void {
    const hospitalID = currentHospitalID()
    const groups = storedGroupsOf(hospitalID)

    // fill store array
    for (let i = 0; i < array.length; i++) {
        const group = array[i]
        const groupID = group._id
        const storedGroup = groups.find((g) => g._id == groupID)

        if (storedGroup == undefined) {
            // isEmpty
            groups.push({ _id: group._id, isSelected: true, order: groups.length })
        } else if (group.isSelected == undefined) {
            // from network
            group.order = storedGroup.order
            group.isSelected = storedGroup.isSelected
        } else {
            // from user action
            const index = groups.indexOf(storedGroup)
            group.order = storedGroup.order
            groups[index].isSelected = group.isSelected
        }
    }

    // set order & sort
    array.forEach((group) => {
        const g = groups.find((v) => v._id == group._id)
        group.order = g?.order
        group.isSelected = g?.isSelected
    })
    array.sort((l, r) => (l.order as number) - (r.order as number))

    // store
    store.commit('MU_V2Reservation_UpdateGroups', { hospitalID, groups })

    sheetFilters.groups = deepCopy(array) ?? []
}

/**
 * 바꾼 순서·선택을 기억에 쓰고 목록을 다시 맞춘다 (구 updateGroups).
 * 인자는 vuex mutation 이 받는 모양({ hospitalID, groups }) 이다.
 */
export function saveGroups(data: { hospitalID: string; groups: FilterItem[] }): void {
    store.commit('MU_V2Reservation_UpdateGroups', deepCopy(data))

    const hospitalID = currentHospitalID()
    const stored = storedGroupsOf(hospitalID)
    const newArray = (deepCopy(sheetFilters.groups) ?? []) as FilterItem[]
    newArray.forEach((g) => {
        const storedG = stored.find((s) => s._id == g._id)
        g.order = storedG?.order
        g.isSelected = storedG?.isSelected
    })
    newArray.sort((l, r) => ((l.order as number) <= (r.order as number) ? -1 : 1))

    sheetFilters.groups = deepCopy(newArray) ?? []
}

/** 하나 켜고 끄기 (구 updateSelectedGroup) */
export function toggleGroup(index: number): void {
    const newObject = deepCopy(sheetFilters.groups[index]) as FilterItem
    newObject.isSelected = !newObject.isSelected
    sheetFilters.groups.splice(index, 1, newObject)
    setGroups(deepCopy(sheetFilters.groups) ?? [])
}

/** 전부 켜거나 끄기 (구 updateAllSelectedGroup) */
export function toggleAllGroups(isSelected: boolean): void {
    const groups = (deepCopy(sheetFilters.groups) ?? []) as FilterItem[]
    groups.forEach((g) => (g.isSelected = isSelected))
    setGroups(groups)
}

/** AI 그룹이 하나라도 있나 (구 hasAiGroup getter) */
export function hasAiGroup(): boolean {
    return sheetFilters.groups.some((g) => g.isAi)
}

/** 켜져 있는 것만 (구 selectedGroups getter) */
export function selectedGroups(): FilterItem[] {
    return sheetFilters.groups.filter((g) => g.isSelected == true)
}
